// pages/Project/Validation.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { Button, Form, Spinner } from 'react-bootstrap';
import { Tabs, Tab } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import PaginatedListTable from '../../components/PaginatedListTable';
import ImageViewerModal from '../../components/ImageViewerModal';
import ViewEngagementModal from '../../components/ViewEngagementModal';
import Container from '../../layouts/Container';

import api from '../../services/api';
import { getToday } from '../../utils/dateUtil';
import { useProjects } from '../../context/ProjectContext';
import { useUsers } from '../../context/UserContext';

import { getEngagementsByFormGroup } from '../../services/project/formGroupService';
import { updateEngagementStatus } from '../../services/project/engagementService';
import { deleteEngagement } from '../../services/project/engagementService';
import AnchorTag from '../../components/AnchorTag';
import { XOctagon } from 'react-bootstrap-icons';

function Validation() {
  const [activeTab, setActiveTab] = useState('unvalidated');
  // Handler for Validate button
  const handleValidate = async (engagement, remark = '') => {
    try {
      await updateEngagementStatus(engagement.id, 'validated', 'validationRemarks', remark);
      toast.success('Validated!');
      setShowModal(false);
      validatedTableRef.current?.refetch();
      approvedTableRef.current?.refetch();
    } catch (err) {
      toast.error('Validation failed');
    }
  };

  // Handler for Reject button
  const handleReject = async (engagement, remark = '') => {
    try {
      await updateEngagementStatus(engagement.id, 'reject', 'validationRemarks', remark);
      toast.error('Rejected!');
      setShowModal(false);
      validatedTableRef.current?.refetch();
      approvedTableRef.current?.refetch();
    } catch (err) {
      toast.error('Rejection failed');
    }
  };
  const approvedTableRef = useRef();
  const validatedTableRef = useRef();
  const { projectId } = useParams();
  const { projects, isProjectReadOnly } = useProjects();
  const { isSuperAdmin } = useUsers();
  const currentProject = projects?.find(p => String(p.id) === String(projectId));
  const isApprovalEnabled = currentProject?.settings?.approval === true;

  const [startDate, setStartDate] = useState(getToday());
  const [endDate, setEndDate] = useState(getToday());
  const [formGroups, setFormGroups] = useState([]);
  const [selectedFormGroupId, setSelectedFormGroupId] = useState(null);
  const [forms, setForms] = useState([]);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [engagement, setEngagement] = useState(null);
  const [loadingFG, setLoadingFG] = useState(true);
  const [loadingApproved, setLoadingApproved] = useState(false);
  const [loadingValidated, setLoadingValidated] = useState(false);
  
  const disapprovedTableRef = useRef();
  const [loadingDisapproved, setLoadingDisapproved] = useState(false);
  const rejectedTableRef = useRef();
  const [loadingRejected, setLoadingRejected] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const res = await api.get(`/projects/${projectId}/formgroups`);
        setFormGroups(res.data || []);
        const activeGroup = (res.data || []).find(g => g.isActive) || (res.data || [])[0];
        if (activeGroup) {
          const fgId = String(activeGroup.id);
          setSelectedFormGroupId(fgId);
          await fetchForms(fgId);
        }
      } catch (err) {
        console.error(err);
        toast.error('Failed to load form groups');
      } finally {
        setLoadingFG(false);
      }
    };
    init();
  }, [projectId]);

  const fetchForms = async (formGroupId) => {
    try {
      const res = await api.get(`/projects/${projectId}/formgroups/${formGroupId}`);
      setForms(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load forms');
    }
  };

  const handleApply = async () => {
    if (selectedFormGroupId) await fetchForms(selectedFormGroupId);
    approvedTableRef.current?.refetch();
    validatedTableRef.current?.refetch();
    if (isApprovalEnabled) {
      disapprovedTableRef.current?.refetch();
    }
    rejectedTableRef.current?.refetch();
  };

  const handleDelete = async (engagementId, tab) => {
    if (!window.confirm('Are you sure you want to delete this engagement?')) return;
    try {
      const response = await deleteEngagement(engagementId);
      if (!response.success) {
        toast.error(response.data?.error || 'Delete failed');
        return;
      }
      if (tab === 'unvalidated') {
        approvedTableRef.current?.refetch();
      } else if (tab === 'validated') {
        validatedTableRef.current?.refetch();
      } else if (tab === 'disapproved') {
        disapprovedTableRef.current?.refetch();
      } else {
        rejectedTableRef.current?.refetch();
      }
      toast.success(response.message || 'Deleted');
    } catch (error) {
      console.error('Error deleting engagement:', error);
      toast.error(error.response?.data?.errorMessage || 'Something went wrong');
    }
  };

  const viewEngagement = (row) => {
    setEngagement(row);
    setShowModal(true);
  };

  const handleImageClick = (imageSrc, footer) => {
    setSelectedImage({
      src: imageSrc,
      footer
    });
    setShowImageModal(true);
  };

  const columnHelper = createColumnHelper();
  const fieldColumns = useMemo(() => {
    const formWithFields = forms.find(f => Array.isArray(f.fields) && f.fields.length > 0);
    if (!formWithFields) return [];
    return formWithFields.fields
      .filter(field => field.type !== 'label' && field.type !== 'address')
      .slice(0, 4)
      .map(field =>
        columnHelper.accessor(
          row => {
            const value = row.fields?.[field.name];
            return value;
          },
          {
            id: field.name,
            header: field.label || field.name,
            cell: info => {
              const value = info.getValue();
              if (field.type === 'image') {
                return value ? (
                  <img
                    src={value?.uri?.[0]}
                    alt=""
                    style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4 }}
                    onClick={() => handleImageClick(value)}
                  />
                ) : 'No image';
              }
              return value;
            },
          }
        )
      );
  }, [forms]);

  const detailColumns = [
    columnHelper.accessor(
      row => `${row.User?.firstName || ''} ${row.User?.lastName || ''}`.trim(),
      {
        id: 'fullName',
        header: 'Reported By',
      }
    ),
    columnHelper.accessor(row => row.createdAt, {
      id: 'createdAt',
      header: 'Synced Date',
      cell: info => new Date(info.getValue()).toLocaleString(),
    }),
    columnHelper.accessor(row => row.submittedAt, {
      id: 'submittedAt',
      header: 'Submitted Date',
      cell: info => info.getValue() ? new Date(info.getValue()).toLocaleString() : '—',
    }),
    (!isProjectReadOnly && isSuperAdmin) && {
      id: 'actions',
      header: 'Actions',
      meta: { sticky: true },
      cell: ({ row }) => (
        <AnchorTag onClick={() => handleDelete(row.original.id)} hoverText="Delete">
          <XOctagon />
        </AnchorTag>
      ),
    },
  ].filter(Boolean);

  
  const fetchDisapprovedEngagements = async (params) => {
    setLoadingDisapproved(true);
    try {
      const startDateUTC = new Date(`${startDate}T00:00:00+08:00`).toISOString();
      const endDateUTC = new Date(`${endDate}T23:59:59+08:00`).toISOString();
      const res = await getEngagementsByFormGroup(projectId, {
        formGroupId: selectedFormGroupId,
        startDate: startDateUTC,
        endDate: endDateUTC,
        status: 'disapproved',
        ...params,
      });
      return res;
    } catch (err) {
      console.error(err);
      toast.error('Error fetching engagements.');
      return { data: [], totalPages: 0 };
    } finally {
      setLoadingDisapproved(false);
    }
  };

  const fetchRejectedEngagements = async (params) => {
    setLoadingRejected(true);
    try {
      const startDateUTC = new Date(`${startDate}T00:00:00+08:00`).toISOString();
      const endDateUTC = new Date(`${endDate}T23:59:59+08:00`).toISOString();
      const res = await getEngagementsByFormGroup(projectId, {
        formGroupId: selectedFormGroupId,
        startDate: startDateUTC,
        endDate: endDateUTC,
        status: 'reject',
        ...params,
      });
      return res;
    } catch (err) {
      console.error(err);
      toast.error('Error fetching engagements.');
      return { data: [], totalPages: 0 };
    } finally {
      setLoadingRejected(false);
    }
  };

  // Fetchers for two tabs only
  const fetchApprovedEngagements = async (params) => {
    setLoadingApproved(true);
    try {
      const startDateUTC = new Date(`${startDate}T00:00:00+08:00`).toISOString();
      const endDateUTC = new Date(`${endDate}T23:59:59+08:00`).toISOString();
      const res = await getEngagementsByFormGroup(projectId, {
        formGroupId: selectedFormGroupId,
        startDate: startDateUTC,
        endDate: endDateUTC,
        status: 'approved',
        ...params,
      });
      return res;
    } catch (err) {
      console.error(err);
      toast.error('Error fetching engagements.');
      return { data: [], totalPages: 0 };
    } finally {
      setLoadingApproved(false);
    }
  };

  const fetchValidatedEngagements = async (params) => {
    setLoadingValidated(true);
    try {
      const startDateUTC = new Date(`${startDate}T00:00:00+08:00`).toISOString();
      const endDateUTC = new Date(`${endDate}T23:59:59+08:00`).toISOString();
      const res = await getEngagementsByFormGroup(projectId, {
        formGroupId: selectedFormGroupId,
        startDate: startDateUTC,
        endDate: endDateUTC,
        status: 'validated',
        ...params,
      });
      return res;
    } catch (err) {
      console.error(err);
      toast.error('Error fetching engagements.');
      return { data: [], totalPages: 0 };
    } finally {
      setLoadingValidated(false);
    }
  };

  useEffect(() => {
    if (!isApprovalEnabled && activeTab === 'disapproved') {
      setActiveTab('unvalidated');
    }
  }, [activeTab, isApprovalEnabled]);

  return (
    <Container>
      <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
        <h2 className="mb-0">Validation</h2>
        <Form className="d-flex align-items-center gap-3 flex-wrap mb-0">
          <Form.Group className="d-flex align-items-center gap-2 mb-0">
            <Form.Label className="mb-0">Start</Form.Label>
            <Form.Control
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="d-flex align-items-center gap-2 mb-0">
            <Form.Label className="mb-0">End</Form.Label>
            <Form.Control
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="d-flex align-items-center gap-2 mb-0">
            <Form.Label className="mb-0" style={{ whiteSpace: 'nowrap' }}>
              Form Group
            </Form.Label>
            <Form.Select
              value={selectedFormGroupId || ''}
              onChange={async e => {
                const fgId = e.target.value;
                setSelectedFormGroupId(fgId);
                await fetchForms(fgId);
                approvedTableRef.current?.refetch({ page: 0 });
                validatedTableRef.current?.refetch({ page: 0 });
                if (isApprovalEnabled) {
                  disapprovedTableRef.current?.refetch({ page: 0 });
                }
                rejectedTableRef.current?.refetch({ page: 0 });
              }}
              disabled={loadingFG || !formGroups.length}
            >
              {(formGroups || []).map(g => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Button variant="primary" type="button" onClick={handleApply}>
            Apply
          </Button>
        </Form>
      </div>
      <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-3">
        <Tab eventKey="unvalidated" title="Unvalidated">
          {loadingFG ? (
            <p>Loading...</p>
          ) : (
            <PaginatedListTable
              ref={approvedTableRef}
              tableId="approval-list"
              columns={[...fieldColumns, ...detailColumns]}
              rowIdAccessor={row => row.id}
              onRowClick={viewEngagement}
              onFetchData={fetchApprovedEngagements}
              defaultSort={[["submittedAt", "DESC"]]}
              isLoading={loadingApproved}
            />
          )}
        </Tab>
        <Tab eventKey="validated" title="Validated">
          {loadingFG ? (
            <p>Loading...</p>
          ) : (
            <PaginatedListTable
              ref={validatedTableRef}
              tableId="validated-list"
              columns={[...fieldColumns, ...detailColumns]}
              rowIdAccessor={row => row.id}
              onRowClick={viewEngagement}
              onFetchData={fetchValidatedEngagements}
              defaultSort={[["submittedAt", "DESC"]]}
              isLoading={loadingValidated}
            />
          )}
        </Tab>
        {isApprovalEnabled && (
          <Tab eventKey="disapproved" title="Disapproved">
            {loadingFG ? (
              <p>Loading...</p>
            ) : (
              <PaginatedListTable
                ref={disapprovedTableRef}
                tableId="disapproved-list"
                columns={[...fieldColumns, ...detailColumns]}
                rowIdAccessor={row => row.id}
                onRowClick={viewEngagement}
                onFetchData={fetchDisapprovedEngagements}
                defaultSort={[["submittedAt", "DESC"]]}
                isLoading={loadingDisapproved}
              />
            )}
          </Tab>
        )}
        <Tab eventKey="rejected" title="Rejected">
          {loadingFG ? (
            <p>Loading...</p>
          ) : (
            <PaginatedListTable
              ref={rejectedTableRef}
              tableId="rejected-list"
              columns={[...fieldColumns, ...detailColumns]}
              rowIdAccessor={row => row.id}
              onRowClick={viewEngagement}
              onFetchData={fetchRejectedEngagements}
              defaultSort={[["submittedAt", "DESC"]]}
              isLoading={loadingRejected}
            />
          )}
        </Tab>
      </Tabs>
      <ViewEngagementModal
        show={showModal}
        forms={forms}
        engagement={engagement ? { ...engagement, fromValidationPage: activeTab === 'unvalidated' } : null}
        handleImageClick={handleImageClick}
        onClose={() => setShowModal(false)}
        onValidate={activeTab === 'unvalidated' ? handleValidate : undefined}
        onReject={activeTab === 'unvalidated' ? handleReject : undefined}
      />
      {showImageModal && selectedImage && (
        <ImageViewerModal
          show={showImageModal}
          imageUrl={selectedImage.src}
          footer={selectedImage.footer}
          onClose={() => setShowImageModal(false)}
        />
      )}
    </Container>
  );
}
export default Validation;
